import { useCallback, useMemo, useRef, useState } from 'react'
import type {
  IdentityFieldPatchResponse,
  IdentityFieldSummary,
  IdentityRequiredFieldsProjection,
} from '@digital-twins/shared-types'

interface UseIdentityFieldListStateArgs {
  machineVersion: number | undefined
  requiredFields: IdentityRequiredFieldsProjection | null | undefined
}

export function useIdentityFieldListState(args: UseIdentityFieldListStateArgs) {
  const { requiredFields: serverRequiredFields } = args
  const [optimisticOverrides, setOptimisticOverrides] = useState<
    Map<string, IdentityFieldSummary>
  >(new Map())
  const lastAppliedVersionRef = useRef<number | null>(null)

  const requiredFields = serverRequiredFields ?? null

  const fields = useMemo(() => {
    const items = requiredFields?.items ?? []
    if (optimisticOverrides.size === 0) return items
    return items.map((field) => optimisticOverrides.get(field.canonicalId) ?? field)
  }, [optimisticOverrides, requiredFields?.items])

  const applyPatchResponse = useCallback(
    (
      response: IdentityFieldPatchResponse,
      optimisticField?: IdentityFieldSummary | null,
    ) => {
      if (
        lastAppliedVersionRef.current !== null &&
        response.machineVersion <= lastAppliedVersionRef.current
      ) {
        return
      }
      lastAppliedVersionRef.current = response.machineVersion
      setOptimisticOverrides((prev) => {
        const next = new Map(prev)
        next.set(
          response.field.canonicalId,
          optimisticField ?? response.field,
        )
        return next
      })
    },
    [],
  )

  return {
    requiredFields,
    fields,
    applyPatchResponse,
  }
}
